// The localization pages' shared logic (spec/dashboard.md sections 9.1–9.6): the strings as the user sees them (the
// drafts over the catalog, new drafts first), the staged edits, the coverage counts, the CSV writer and a Blob download.
import { useMemo } from 'react';
import type { Json, LocalizationString } from '../../api/types';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey, type Draft, type Workspace } from '../../workspace/Workspace';
import type { ContentCatalog } from '../ContentContext';

export type StringRow = LocalizationString;

/** The draft template word of strings in the Changes panel (section 4.5). */
export const STRING_TEMPLATE = 'Localization';
export const DEFAULT_GROUP = 'DEFAULT';

export function fullKeyOf(s: Pick<StringRow, 'group' | 'key'>): string {
  return `${s.group}/${s.key}`;
}

export function valuesOf(s: StringRow): Record<string, string> {
  return (s.values ?? {}) as Record<string, string>;
}

export function hasText(s: StringRow, code: string): boolean {
  const v = valuesOf(s)[code];
  return typeof v === 'string' && v.length > 0;
}

/** Plain string order (the API's order: `100000_COINS` before `1000_COINS`). */
export function ordinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Every string of the branch with its draft overlaid; new drafts first (newest on top), then the catalog by GROUP/KEY. */
export function useStringRows(catalog: ContentCatalog): { rows: StringRow[]; draftOf: (id: string) => Draft | undefined } {
  const workspace = useWorkspace();
  const version = useWorkspaceVersion(workspace);
  const rows = useMemo(() => {
    const fresh = workspace.all
      .filter((d) => d.kind === 'string' && d.status === 'new')
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((d) => d.current as unknown as StringRow);
    const server = [...catalog.stringList].sort((a, b) => ordinal(a.fullKey ?? fullKeyOf(a), b.fullKey ?? fullKeyOf(b))).map((s) => (workspace.getDraft(entityKey('string', s.id))?.current as unknown as StringRow | undefined) ?? s);
    return [...fresh, ...server];
  }, [workspace, catalog.stringList, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const draftOf = (id: string) => workspace.getDraft(entityKey('string', id));
  return { rows, draftOf };
}

/** Stages one field of a string (`values.<code>`, `key`, `group`, `description`, `export`) on its draft. */
export function editString(workspace: Workspace, catalog: ContentCatalog, row: StringRow, path: string, value: unknown): void {
  const server = catalog.strings.get(row.id);
  const existing = workspace.getDraft(entityKey('string', row.id));
  const next = { ...row, ...(path === 'key' ? { key: String(value) } : path === 'group' ? { group: String(value) } : {}) };
  const label = fullKeyOf(next);
  if (existing) workspace.ensure('string', row.id, { version: null, value: null }, { label }); // the panel's label follows a rename
  workspace.edit('string', row.id, path, value, { base: server ? { version: Number(server.version), value: server as unknown as Json } : undefined, label, template: STRING_TEMPLATE });
}

/** A new string draft at the top of the grid: nothing but the group (the key cell is focused; the value waits to be typed). */
export function createStringDraft(workspace: Workspace, group: string, defaultLanguage?: string): string {
  // `defaultLanguage` lets Save All refuse a row without its default-language value (section 9.1); the batch operation never carries it
  return workspace.create('string', { id: '', group, key: '', description: null, export: true, values: {}, ...(defaultLanguage ? { defaultLanguage } : {}) } as unknown as Json, { label: `${group}/(new string)`, template: STRING_TEMPLATE });
}

/** A complete new string staged from a field (section 6.4: the text typed where the string is used); returns its temp id. */
export function createNamedString(workspace: Workspace, s: { group: string; key: string; values: Record<string, string>; description?: string | null; export?: boolean }, defaultLanguage?: string): string {
  const value = { id: '', group: s.group, key: s.key, description: s.description ?? null, export: s.export ?? true, values: s.values, ...(defaultLanguage ? { defaultLanguage } : {}) } as unknown as Json;
  return workspace.create('string', value, { label: fullKeyOf(s), template: STRING_TEMPLATE });
}

/** The keys of a group as the user sees them: the catalog's and the staged drafts' (a deleted draft frees its key). */
export function keysInGroup(workspace: Workspace, catalog: ContentCatalog, group: string): Set<string> {
  const keys = new Set<string>();
  const deleted = new Set<string>();
  for (const d of workspace.all) {
    if (d.kind !== 'string') continue;
    const s = d.current as unknown as StringRow | null;
    if (d.status === 'deleted') deleted.add(d.id);
    else if (s && s.group === group) keys.add(s.key);
  }
  for (const s of catalog.stringList) if (s.group === group && !deleted.has(s.id) && !workspace.getDraft(entityKey('string', s.id))) keys.add(s.key);
  return keys;
}

export function removeString(workspace: Workspace, catalog: ContentCatalog, row: StringRow): void {
  const server = catalog.strings.get(row.id);
  workspace.remove('string', row.id, server ? { version: Number(server.version), value: server as unknown as Json } : undefined, { label: fullKeyOf(row), template: STRING_TEMPLATE });
}

export interface Coverage {
  code: string;
  name: string;
  isDefault: boolean;
  total: number;
  translated: number;
  missing: number;
  /** 0–100 */
  percent: number;
  missingRows: StringRow[];
}

/** Per enabled language over the exported strings (section 9.5); the default language reads 100 % by definition. */
export function coverageOf(rows: StringRow[], languages: { code: string; name: string }[], defaultLanguage: string): Coverage[] {
  const exported = rows.filter((r) => r.export !== false);
  return languages.map((l) => {
    const isDefault = l.code === defaultLanguage;
    const missingRows = isDefault ? [] : exported.filter((r) => !hasText(r, l.code));
    const total = exported.length;
    const translated = isDefault ? total : total - missingRows.length;
    return { code: l.code, name: l.name, isDefault, total, translated, missing: total - translated, percent: isDefault || total === 0 ? 100 : Math.floor((translated / total) * 1000) / 10, missingRows };
  });
}

/** RFC 4180: a field with a comma, a quote or a line break is quoted, quotes doubled. */
export function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvField).join(',')).join('\r\n') + '\r\n';
}

/** Hands the browser a file to save (a Blob URL on a temporary link). */
export function downloadText(filename: string, text: string, type = 'text/csv'): void {
  try {
    const blob = new Blob([text], { type: `${type};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    // no Blob URLs (an old browser, a test without them): nothing to save
  }
}

/** A group or key the API accepts: no `/`, no control characters, no surrounding spaces, not empty. */
export function validName(text: string): string | null {
  if (text.length === 0) return 'Required.';
  if (text !== text.trim()) return 'No leading or trailing spaces.';
  if (text.includes('/')) return 'No "/".';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(text)) return 'No control characters.';
  return null;
}
